/**
 * Reporte: Cuenta corriente — Fase 7.3 (reporte 2 de 7)
 *
 * Dos modos según filtro:
 *   - Sin empresa seleccionada → **resumen por empresa** (una fila por
 *     empresa, con saldos agregados).
 *   - Con empresa seleccionada → **detalle por cupón** (una fila por cupón
 *     de esa empresa, con monto emitido / pagado / saldo).
 *
 * Reutiliza las vistas SQL `vw_cuenta_corriente_resumen` y
 * `vw_cuenta_corriente_detalle` (decisión #030).
 */

import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  TextField,
  Autocomplete,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

import { useEmpresas } from '../../hooks/useEmpresas';
import {
  useCuentaCorrienteResumen,
  useCuentaCorrienteDetalle,
} from '../../hooks/useCuentaCorriente';
import { formatCurrency, formatDate, formatCuit } from '../../utils/format';
import {
  type CuentaCorrienteDetalle,
  type CuentaCorrienteResumen,
  getEstadoCtaCteLabel,
  getEstadoCtaCteColor,
} from '../../types/cuentaCorriente';

import {
  createReportePdf,
  addReporteTable,
  addReporteFooter,
  fmtCurrency,
  fmtDate,
  fmtText,
  type ColumnaPdf,
} from '../../utils/reportes/pdf';
import { generarReporteExcel, type ColumnaExcel } from '../../utils/reportes/excel';
import { triggerBlobDownload, buildReporteFilename } from '../../utils/reportes/download';

// ── Componente ────────────────────────────────────────────────────────────────

export default function CuentaCorrienteReporte() {
  // ── Filtros ────────────────────────────────────────────────────────────
  const [empresaId, setEmpresaId] = useState<string | null>(null);

  // ── Datos ──────────────────────────────────────────────────────────────
  const { data: empresasResult } = useEmpresas({ pageSize: 1000 });
  const empresas = empresasResult?.data ?? [];

  const { data: resumen = [], isLoading: loadingResumen, isError: errorResumen } =
    useCuentaCorrienteResumen();
  const {
    data: detalle = [],
    isLoading: loadingDetalle,
    isError: errorDetalle,
  } = useCuentaCorrienteDetalle(empresaId ?? undefined);

  const modo: 'resumen' | 'detalle' = empresaId ? 'detalle' : 'resumen';
  const isLoading = modo === 'resumen' ? loadingResumen : loadingDetalle;
  const isError = modo === 'resumen' ? errorResumen : errorDetalle;

  // ── Filtrado y orden ───────────────────────────────────────────────────
  const resumenFiltrado = useMemo(
    () =>
      [...resumen].sort((a, b) => b.saldo_total - a.saldo_total),
    [resumen],
  );

  // ── Totales ────────────────────────────────────────────────────────────
  const totales = useMemo(() => {
    if (modo === 'resumen') {
      return {
        cantidad: resumenFiltrado.length,
        totalSaldo: resumenFiltrado.reduce((s, r) => s + Number(r.saldo_total), 0),
        totalEmitido: resumenFiltrado.reduce(
          (s, r) => s + Number(r.monto_emitido_total),
          0,
        ),
        totalPagado: resumenFiltrado.reduce(
          (s, r) => s + Number(r.monto_pagado_total),
          0,
        ),
      };
    }
    return {
      cantidad: detalle.length,
      totalSaldo: detalle.reduce((s, r) => s + Number(r.saldo), 0),
      totalEmitido: detalle.reduce((s, r) => s + Number(r.monto_emitido), 0),
      totalPagado: detalle.reduce((s, r) => s + Number(r.monto_pagado), 0),
    };
  }, [modo, resumenFiltrado, detalle]);

  const empresaSeleccionada = empresas.find((e) => e.id === empresaId);

  const subtitulo = useMemo(() => {
    if (modo === 'detalle' && empresaSeleccionada) {
      return `Empresa: ${empresaSeleccionada.razon_social} (CUIT ${formatCuit(empresaSeleccionada.cuit)})`;
    }
    return 'Resumen de todas las empresas';
  }, [modo, empresaSeleccionada]);

  // ── Export PDF ─────────────────────────────────────────────────────────
  const handleExportPdf = () => {
    const doc = createReportePdf({
      titulo: 'Cuenta corriente',
      subtitulo,
      orientacion: 'landscape',
    });

    let columnas: ColumnaPdf[];
    let body: unknown[][];

    if (modo === 'resumen') {
      columnas = [
        { header: 'Empresa', format: fmtText },
        { header: 'CUIT', align: 'center', format: fmtText },
        { header: 'Cupones', align: 'right', format: fmtText },
        { header: 'Pendientes', align: 'right', format: fmtText },
        { header: 'Vencidos', align: 'right', format: fmtText },
        { header: 'Emitido', align: 'right', format: fmtCurrency },
        { header: 'Pagado', align: 'right', format: fmtCurrency },
        { header: 'Saldo', align: 'right', format: fmtCurrency },
        { header: 'Estado', align: 'center', format: (v) => getEstadoCtaCteLabel(String(v)) },
      ];
      body = resumenFiltrado.map((r) => [
        r.empresa_razon_social,
        formatCuit(r.empresa_cuit),
        r.cant_cupones,
        r.cant_pendientes,
        r.cant_vencidos,
        r.monto_emitido_total,
        r.monto_pagado_total,
        r.saldo_total,
        r.estado_general,
      ]);
      body.push([
        `TOTAL (${totales.cantidad} empresas)`,
        '',
        '',
        '',
        '',
        totales.totalEmitido,
        totales.totalPagado,
        totales.totalSaldo,
        '',
      ]);
    } else {
      columnas = [
        { header: 'Código', format: fmtText },
        { header: 'Período', align: 'center', format: fmtText },
        { header: 'Emisión', align: 'center', format: fmtDate },
        { header: 'Vencimiento', align: 'center', format: fmtDate },
        { header: 'Emitido', align: 'right', format: fmtCurrency },
        { header: 'Pagado', align: 'right', format: fmtCurrency },
        { header: 'Saldo', align: 'right', format: fmtCurrency },
        { header: 'Estado', align: 'center', format: (v) => getEstadoCtaCteLabel(String(v)) },
      ];
      body = detalle.map((r) => [
        r.cupon_codigo,
        r.periodo,
        r.fecha_emision,
        r.fecha_vencimiento,
        r.monto_emitido,
        r.monto_pagado,
        r.saldo,
        r.estado_cta_cte,
      ]);
      body.push([
        `TOTAL (${totales.cantidad} cupones)`,
        '',
        '',
        '',
        totales.totalEmitido,
        totales.totalPagado,
        totales.totalSaldo,
        '',
      ]);
    }

    addReporteTable(doc, columnas, body, {
      titulo: 'Cuenta corriente',
      subtitulo,
    });
    addReporteFooter(doc);

    const blob = doc.output('blob');
    const nombre =
      modo === 'detalle' && empresaSeleccionada
        ? `cuenta-corriente-${empresaSeleccionada.razon_social}`
        : 'cuenta-corriente-resumen';
    triggerBlobDownload(blob, buildReporteFilename(nombre, 'pdf'));
  };

  // ── Export Excel ───────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    let columnas: ColumnaExcel[];
    let rowsExcel: Record<string, unknown>[];

    if (modo === 'resumen') {
      columnas = [
        { header: 'Empresa', key: 'empresa', width: 35, format: 'text' },
        { header: 'CUIT', key: 'cuit', width: 14, format: 'text' },
        { header: 'Cupones', key: 'cupones', width: 10, format: 'integer' },
        { header: 'Pendientes', key: 'pendientes', width: 12, format: 'integer' },
        { header: 'Vencidos', key: 'vencidos', width: 10, format: 'integer' },
        { header: 'Pagados', key: 'pagados', width: 10, format: 'integer' },
        { header: 'Emitido', key: 'emitido', width: 14, format: 'currency' },
        { header: 'Pagado', key: 'pagado', width: 14, format: 'currency' },
        { header: 'Saldo', key: 'saldo', width: 14, format: 'currency' },
        { header: 'Estado', key: 'estado', width: 16, format: 'text' },
      ];
      rowsExcel = resumenFiltrado.map((r) => ({
        empresa: r.empresa_razon_social,
        cuit: formatCuit(r.empresa_cuit),
        cupones: r.cant_cupones,
        pendientes: r.cant_pendientes,
        vencidos: r.cant_vencidos,
        pagados: r.cant_pagados,
        emitido: r.monto_emitido_total,
        pagado: r.monto_pagado_total,
        saldo: r.saldo_total,
        estado: getEstadoCtaCteLabel(r.estado_general),
      }));
    } else {
      columnas = [
        { header: 'Código', key: 'codigo', width: 28, format: 'text' },
        { header: 'Período', key: 'periodo', width: 10, format: 'text' },
        { header: 'Emisión', key: 'emision', width: 12, format: 'date' },
        { header: 'Vencimiento', key: 'vencimiento', width: 12, format: 'date' },
        { header: 'Aporte', key: 'aporte', width: 14, format: 'currency' },
        { header: 'Interés', key: 'interes', width: 14, format: 'currency' },
        { header: 'Emitido', key: 'emitido', width: 14, format: 'currency' },
        { header: 'Pagado', key: 'pagado', width: 14, format: 'currency' },
        { header: 'Saldo', key: 'saldo', width: 14, format: 'currency' },
        { header: 'Estado', key: 'estado', width: 14, format: 'text' },
      ];
      rowsExcel = detalle.map((r) => ({
        codigo: r.cupon_codigo,
        periodo: r.periodo,
        emision: new Date(r.fecha_emision),
        vencimiento: new Date(r.fecha_vencimiento),
        aporte: r.monto_aporte,
        interes: r.interes,
        emitido: r.monto_emitido,
        pagado: r.monto_pagado,
        saldo: r.saldo,
        estado: getEstadoCtaCteLabel(r.estado_cta_cte),
      }));
    }

    const blob = await generarReporteExcel({
      titulo: 'Cuenta corriente',
      subtitulo,
      nombreHoja: modo === 'resumen' ? 'Resumen' : 'Detalle',
      columnas,
      rows: rowsExcel,
    });
    const nombre =
      modo === 'detalle' && empresaSeleccionada
        ? `cuenta-corriente-${empresaSeleccionada.razon_social}`
        : 'cuenta-corriente-resumen';
    triggerBlobDownload(blob, buildReporteFilename(nombre, 'xlsx'));
  };

  // ── Columnas DataGrid ──────────────────────────────────────────────────
  const columnsResumen: GridColDef<CuentaCorrienteResumen>[] = [
    { field: 'empresa_razon_social', headerName: 'Empresa', flex: 2, minWidth: 220 },
    {
      field: 'empresa_cuit',
      headerName: 'CUIT',
      width: 130,
      renderCell: (p) => formatCuit(p.row.empresa_cuit),
    },
    {
      field: 'cant_cupones',
      headerName: 'Cupones',
      width: 90,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'cant_pendientes',
      headerName: 'Pendientes',
      width: 100,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'cant_vencidos',
      headerName: 'Vencidos',
      width: 100,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'monto_pagado_total',
      headerName: 'Pagado',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) =>
        formatCurrency(p.row.monto_pagado_total),
    },
    {
      field: 'saldo_total',
      headerName: 'Saldo',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) => (
        <Typography
          variant="body2"
          fontWeight={600}
          color={p.row.saldo_total > 0 ? 'warning.main' : 'inherit'}
        >
          {formatCurrency(p.row.saldo_total)}
        </Typography>
      ),
    },
    {
      field: 'estado_general',
      headerName: 'Estado',
      width: 140,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteResumen>) => (
        <Chip
          label={getEstadoCtaCteLabel(p.row.estado_general)}
          color={getEstadoCtaCteColor(p.row.estado_general)}
          size="small"
        />
      ),
    },
  ];

  const columnsDetalle: GridColDef<CuentaCorrienteDetalle>[] = [
    {
      field: 'cupon_codigo',
      headerName: 'Código',
      width: 230,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Typography variant="body2" fontFamily="monospace" fontSize={13}>
          {p.row.cupon_codigo}
        </Typography>
      ),
    },
    { field: 'periodo', headerName: 'Período', width: 100 },
    {
      field: 'fecha_vencimiento',
      headerName: 'Vencimiento',
      width: 120,
      renderCell: (p) => formatDate(p.row.fecha_vencimiento),
    },
    {
      field: 'monto_emitido',
      headerName: 'Emitido',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => formatCurrency(p.row.monto_emitido),
    },
    {
      field: 'monto_pagado',
      headerName: 'Pagado',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => formatCurrency(p.row.monto_pagado),
    },
    {
      field: 'saldo',
      headerName: 'Saldo',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Typography
          variant="body2"
          fontWeight={600}
          color={p.row.saldo > 0 ? 'warning.main' : 'inherit'}
        >
          {formatCurrency(p.row.saldo)}
        </Typography>
      ),
    },
    {
      field: 'estado_cta_cte',
      headerName: 'Estado',
      width: 130,
      renderCell: (p: GridRenderCellParams<CuentaCorrienteDetalle>) => (
        <Chip
          label={getEstadoCtaCteLabel(p.row.estado_cta_cte)}
          color={getEstadoCtaCteColor(p.row.estado_cta_cte)}
          size="small"
        />
      ),
    },
  ];

  const rowsDataGrid = modo === 'resumen' ? resumenFiltrado : detalle;
  const getRowId = modo === 'resumen'
    ? (r: CuentaCorrienteResumen) => r.empresa_id
    : (r: CuentaCorrienteDetalle) => r.cupon_id;

  const sinDatos = !isLoading && !isError && rowsDataGrid.length === 0;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/reportes"
        sx={{ mb: 2 }}
      >
        Volver a Reportes
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <AccountBalanceWalletIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Cuenta corriente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Resumen por empresa o detalle por cupón (seleccioná una empresa
            para ver el detalle).
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Filtros
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Autocomplete
            sx={{ minWidth: 320 }}
            size="small"
            options={empresas}
            getOptionLabel={(opt) => opt.razon_social}
            value={empresas.find((e) => e.id === empresaId) ?? null}
            onChange={(_, val) => setEmpresaId(val?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa (vacío = resumen de todas)"
              />
            )}
          />
          <Chip
            label={modo === 'detalle' ? 'Modo detalle (1 empresa)' : 'Modo resumen (todas)'}
            color={modo === 'detalle' ? 'info' : 'default'}
            variant="outlined"
            size="small"
          />
        </Stack>
      </Card>

      {/* Totales + Export */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={2}
      >
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {modo === 'resumen' ? 'Empresas' : 'Cupones'}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {totales.cantidad}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total emitido
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {formatCurrency(totales.totalEmitido)}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total pagado
            </Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">
              {formatCurrency(totales.totalPagado)}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Saldo total
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color={totales.totalSaldo > 0 ? 'warning.main' : 'inherit'}
            >
              {formatCurrency(totales.totalSaldo)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={rowsDataGrid.length === 0}
          >
            Descargar PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableViewIcon />}
            onClick={handleExportExcel}
            disabled={rowsDataGrid.length === 0}
          >
            Descargar Excel
          </Button>
        </Stack>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar la cuenta corriente.
        </Alert>
      )}
      {sinDatos && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sin datos para mostrar.
        </Alert>
      )}

      <Card>
        {modo === 'resumen' ? (
          <DataGrid
            rows={rowsDataGrid as CuentaCorrienteResumen[]}
            columns={columnsResumen}
            getRowId={getRowId as (r: CuentaCorrienteResumen) => string}
            loading={isLoading}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={{ border: 0 }}
          />
        ) : (
          <DataGrid
            rows={rowsDataGrid as CuentaCorrienteDetalle[]}
            columns={columnsDetalle}
            getRowId={getRowId as (r: CuentaCorrienteDetalle) => string}
            loading={isLoading}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={{ border: 0 }}
          />
        )}
      </Card>
    </Box>
  );
}
